import { instantOf } from './freshness.js'
import { joinableId } from './music.js'
import { winningTier } from './vocabulary.js'

// "In the server" — the cross-cog activity feed. P6-182, design §4.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THIS MODULE EXISTS BECAUSE A WRITTEN-DOWN CONDITION FIRED, AND THE
//    CONDITION IS WORTH MORE THAN THE FEATURE
// ═══════════════════════════════════════════════════════════════════════════
//
// P6-182 built this on paper on 2026-09-04 and REFUSED it, on a measurement:
// `3a/movie.json` carried exactly three datable events, the movie panel already
// rendered all three plus the pick, and at ~2 events a week five rows reached
// back two and a half weeks. *"A calendar of a weekly event, not a feed."*
//
// It then wrote a falsifiable retirement condition next to the compromise —
// gotcha 6's pattern, the one thing in this repo that has ever stopped a
// compromise becoming permanent by default:
//
//     THE FEED IS RIGHT WHEN A SECOND DATED SOURCE PUBLISHES,
//     NOT WHEN THE MOVIE BLOB GROWS.
//
// ✅ It fired. `music/utils/projection.py` publishes `recent_events` — dated
// `induction`, `soty` and `recap` rows — and Jordan ruled *"let's do the feed"*.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 WHAT THE SECOND SOURCE DID **NOT** ANSWER, MEASURED RATHER THAN ASSUMED
// ═══════════════════════════════════════════════════════════════════════════
//
// Measured by running the real `build_music_projection` over a real store built
// through the real write API, and reading the JSON BYTES it produced — not by
// reading either publisher's source:
//
//   music   10 rows from 8 inductions + 1 recap + 1 ballot, publisher cap 20
//           `at` is AWARE      2026-09-02T10:17:14.922147+00:00
//           `actor_ids` are STRINGS since #426
//   movie    3 datable kinds   finalized_at, ratings_finalized_at, crowned_at
//           all NAIVE          2026-08-27T22:10:00
//
// ⚠️ THE PICK STILL HAS NO DATE. `_night_entry` publishes `week`,
// `finalized_at` and `ratings_finalized_at` and drops the store's own
// `timestamp` and `date`, so *"Sam picked Blade Runner 2049"* — the row the
// DESIGN LEADS WITH — has nothing for its time column. It is therefore not a
// feed row, and it is not invented one either: the panel says it without a
// date, as "Picked for this week", and that stays the only place it is said.
//
// ⚠️ AND THE MOVIE ROWS STILL RESTATE THE PANEL BESIDE THEM. That was the other
// half of the refusal and music does not fix it — stated here rather than
// discovered, because the honest claim is narrower than "the objection is
// gone":
//
//   default branch open (movie)   the newest close, the newest rating and the
//                                 newest MOTY echo three visible blocks. Older
//                                 nights echo a disclosure that is COLLAPSED.
//   every other branch open       no movie block is on screen at all.
//   any branch                    the music rows appear nowhere else unless
//                                 the Music pill is the one open.
//
// 📌 SO WHAT CHANGED IS NOT THAT THE OVERLAP WENT AWAY — IT IS THAT THE OVERLAP
// IS NO LONGER THE WHOLE FEED. One source made this module a second heading
// over the panel's own contents; two sources make it the only place on the page
// where the server's week is in one order.
// `src/test/portal/activity-feed.test.jsx` asserts that against the rendered
// document — every Music row names a track that appears nowhere else while the
// default branch is open — so if a future panel starts restating the feed, the
// build reds instead of the page quietly becoming the thing that was refused.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 A FIELD, NOT A BUS — P6-156's STANDING OBLIGATION
// ═══════════════════════════════════════════════════════════════════════════
//
// *Grow-TOLERANT, not grow-anticipating.* There is no registry, no source
// table to add a row to, no plugin shape and no generic event envelope — all
// of which would be this file designing for a producer that has not asked.
// `music/utils/projection.py` makes the same refusal in its own words and for
// the same ruling.
//
// ✅ V2-7B: THE THIRD PRODUCER ARRIVED, EXACTLY AS PREDICTED. This paragraph
// used to say *"there are two producers, so there are two reader functions
// below and a merge... a third producer arrives as a third function and one
// more line in `activityFeed`."* Watch is that third producer: `watchEvents`
// is the third function, and `activityFeed` gained a third array entry, a
// third `missing` entry, and (V2-7b, added the same slice) a per-source cap
// neither predecessor needed while there were only two of them — see
// `SOURCE_MAX`'s own comment. No registry appeared; the cost was exactly what
// this paragraph said it would be, paid a second time.

export const MOVIE_SOURCE = 'movie'
export const MUSIC_SOURCE = 'music'
export const WATCH_SOURCE = 'watch'

/** The label under each row, and the only place the wording lives. */
export const SOURCE_LABEL = Object.freeze({
  [MOVIE_SOURCE]: 'Movie night',
  [MUSIC_SOURCE]: 'Music',
  [WATCH_SOURCE]: 'Watch',
})

/** Rows on screen before the disclosure. The design's frame: five and `+N more`. */
export const FEED_ROWS = 5

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * V2-7b: THE PER-SOURCE CAP, APPLIED BEFORE `FEED_MAX` EVER SEES A ROW
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The most rows ANY ONE source may contribute to the merge — added the day a
 * THIRD source (watch) did, per spec §6: *"the merged feed caps each source at
 * ten of its twenty rows so movie night keeps its history box."*
 *
 * 🔴 WITHOUT THIS, THE NEWEST SINGLE SOURCE CAN FILL EVERY SLOT. Three sources
 * publish on three independent schedules, so any one of them can be the
 * newest ten, twenty, fifty rows in a row — a burst of `listed` adds on a
 * quiet movie-night week, say — and an uncapped merge would show that one
 * source and nothing else. That is `FEED_MAX`'s own original failure
 * (below) with a third possible offender instead of one.
 *
 * ⚠️ TEN, NOT DERIVED FROM ANYTHING — half of `FEED_MAX`, chosen so three
 * sources capped at ten each (30 candidates) comfortably exceed the 20 the
 * merge keeps. See `FEED_MAX`'s own comment for what capping each source
 * DOES NOT guarantee once there are three of them.
 */
export const SOURCE_MAX = 10

/**
 * The most rows this module will hold at all, across every source, after the
 * per-source cap above has already run.
 *
 * 🔴 IT WAS THE MUSIC PUBLISHER'S OWN CAP (`RECENT_EVENTS = 20`), BEFORE A
 * THIRD SOURCE EXISTED. The movie blob publishes up to 50 nights, which is
 * two datable events each plus every crowned season — so an uncapped merge of
 * movie and music alone would have held ~120 rows, of which everything past
 * music's twentieth would have been MOVIE-ONLY. A feed that silently became
 * single-source as you scrolled is the exact thing this row refused in
 * September, arriving below the fold instead of above it.
 *
 * ⚠️ SO THE CAP IS A CLAIM ABOUT HONESTY, NOT ABOUT PAYLOAD. It is not derived
 * from either publisher's own constant — nothing on this side can read them —
 * and it is not meant to track them automatically; if a publisher ever raises
 * its own cap, this number stops being the reason it is and somebody should
 * re-derive it.
 *
 * ⚠️ V2-7b: WHAT `SOURCE_MAX` GUARANTEES, AND WHAT IT DOES NOT. Capping each
 * source at ten means no ONE source can fill every slot — the failure
 * described above. **It does NOT mean every source is guaranteed a slot**:
 * with three sources, if watch and music each have ten rows newer than every
 * movie row, movie night's own ten are exactly the ones this cap drops, and
 * the merged feed is watch-and-music-only. That is a real, observable trade
 * of capping-then-merging rather than reserving a share per source, and it is
 * stated here rather than fixed — a per-source floor is a decision nobody has
 * asked for yet.
 */
export const FEED_MAX = 20

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 IDS ARE JOINED AS STRINGS OR NOT AT ALL, AND THE REFUSAL IS SHARED
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `joinableId` is imported from `music.js` rather than reimplemented here, and
 * that is P6-177's rule rather than laziness: a second snowflake regex on this
 * page is a second answer to *"is this an id"*, and the two would drift the way
 * `rotation_position` drifted into three spellings.
 *
 * #426 taught the music service to publish `str(...)`, so this path is now
 * DEFENSIVE — but it is not dead. R2 holds the last object the service wrote,
 * and a blob published before that fix is still the live answer until the
 * service next reconciles. A number arriving here is refused, counted, and
 * named on the page; it is never coerced with `String()`, which would produce a
 * well-formed id belonging to nobody or to somebody else.
 */
function actorsOf(values) {
  const raw = Array.isArray(values) ? values : []
  const ids = raw.map(joinableId).filter((id) => id !== null)
  // ⚠️ "WAS A PRESENT ID UNUSABLE", not "is one absent". An event the bot
  // performed on its own has no actors and that is a measured emptiness, not a
  // dropped id — `music.js::idsWereDropped` draws the same distinction for the
  // panel and for the same reason.
  const dropped = raw.some((v) => v !== null && v !== undefined && joinableId(v) === null)
  return { ids, dropped }
}

function oneActor(value) {
  return actorsOf(value === null || value === undefined ? [] : [value])
}

/**
 * One row, or `null` when it cannot be dated.
 *
 * 🔴 UNDATED IS DROPPED, NEVER RENDERED WITHOUT A TIME. The music publisher
 * refuses to emit one for the same reason and says it best: *"an undated event
 * in a feed sorts nowhere and reads as 'just now'"*. A row with an empty time
 * column would take the position of the newest thing that happened.
 */
function row(source, kind, at, id, extra) {
  const instant = instantOf(at)
  if (instant === null) return null
  return { id: `${source}:${kind}:${id}`, source, kind, at, instant, ...extra }
}

/**
 * The movie blob's three datable events.
 *
 * ⛔ THREE, AND THE LIST IS NOT SHORT BY OMISSION. Re-derived rather than
 * inherited from the refusal:
 *
 *     grep -an '"[a-z_]*_at":' utils/projection_publisher.py
 *
 * (`-a` per gotcha 1 — a `.pyc` truncates the answer and grep still exits 0.)
 * `rotation` is a state snapshot with no history and no time; `joiners` is in
 * the store and WITHHELD on purpose — *"churn, not a schedule"*; `poster_path`
 * is a value and nothing times it. None of those is waiting on a publisher
 * change somebody could just make.
 */
export function movieEvents(shared, userId) {
  const nights = Array.isArray(shared?.nights) ? shared.nights : []
  const seasons = Array.isArray(shared?.moty) ? shared.moty : []
  const rows = []

  nights.forEach((night, index) => {
    if (!night || typeof night !== 'object') return
    const picker = oneActor(night.picker_id)
    const film = typeof night.title === 'string' && night.title.trim() !== '' ? night.title : null
    // The week key is the only id a night always has; `movie_id` can repeat
    // across a rewatch and `number` is absent on a night nobody closed.
    const key = String(night.week ?? night.movie_id ?? index)
    rows.push(
      row(MOVIE_SOURCE, 'night', night.finalized_at, key, {
        film,
        number: Number.isInteger(night.number) ? night.number : null,
        actorIds: picker.ids,
        droppedActor: picker.dropped,
        mine: picker.ids.includes(userId),
      }),
    )
    // 🔴 A SEPARATE ROW FROM THE CLOSE, BECAUSE THEY ARE SEPARATE STAMPS. They
    // usually land the same evening and the feed shows both — which is the
    // publisher's own shape, not a decision taken here. Collapsing them would
    // mean picking one stamp to represent the other, and the two answer
    // different questions.
    const verdict = night.rating && typeof night.rating === 'object' ? night.rating : null
    if (verdict) {
      rows.push(
        row(MOVIE_SOURCE, 'rating', night.ratings_finalized_at, key, {
          film,
          // 🔴 `winningTier` FROM `vocabulary.js`, NEVER A SECOND DERIVATION.
          // P6-172 is this repo's record of what two answers to one derived
          // fact cost — 5.0 and 2.5 on the same member — and the verdict block
          // one file over already renders this exact value from this exact
          // function.
          tier: winningTier(verdict.breakdown),
          votes: Number.isInteger(verdict.votes) ? verdict.votes : null,
          // ⛔ NO AVERAGE, NO FLAIR WORD, NO RANK. P6-172: the room's verdict
          // on a FILM is a tier chip; a number belonging to a PERSON is the
          // thing this page refuses to render.
          actorIds: [],
          droppedActor: false,
          mine: false,
        }),
      )
    }
  })

  seasons.forEach((season, index) => {
    if (!season || typeof season !== 'object') return
    const picker = oneActor(season.picker_id)
    rows.push(
      row(MOVIE_SOURCE, 'moty', season.crowned_at, String(season.season_id ?? index), {
        film: typeof season.title === 'string' && season.title.trim() !== '' ? season.title : null,
        label:
          typeof season.label === 'string' && season.label.trim() !== ''
            ? season.label
            : String(season.season_id ?? ''),
        actorIds: picker.ids,
        droppedActor: picker.dropped,
        mine: picker.ids.includes(userId),
      }),
    )
  })

  return rows.filter((r) => r !== null)
}

/**
 * The kinds `recent_events` can carry, as the publisher names them.
 *
 * ⚠️ AN UNKNOWN KIND IS DROPPED RATHER THAN GIVEN A FALLBACK SENTENCE. A row
 * this page cannot describe is a row it would describe wrongly, and the music
 * service is free to add one before this file learns the word for it.
 */
const MUSIC_KINDS = new Set(['induction', 'soty', 'recap'])

export function musicEvents(shared, userId) {
  const events = Array.isArray(shared?.recent_events) ? shared.recent_events : []
  const rows = []
  events.forEach((event, index) => {
    if (!event || typeof event !== 'object') return
    const kind = typeof event.kind === 'string' ? event.kind : ''
    if (!MUSIC_KINDS.has(kind)) return
    const actors = actorsOf(event.actor_ids)
    rows.push(
      row(MUSIC_SOURCE, kind, event.at, String(event.key ?? event.cadence ?? index), {
        // `title`/`artist` are `null` rather than a fallback label when the
        // store has never seen the key — the publisher's own words: *"inventing
        // a title here would put a guess in a field a human reads as a fact"*.
        title: typeof event.title === 'string' && event.title.trim() !== '' ? event.title : null,
        artist: typeof event.artist === 'string' && event.artist.trim() !== '' ? event.artist : null,
        resolved: event.resolved === true,
        trackKey: typeof event.key === 'string' && event.key.trim() !== '' ? event.key : null,
        year: Number.isInteger(event.year) ? event.year : null,
        cadence: typeof event.cadence === 'string' ? event.cadence : null,
        actorIds: actors.ids,
        droppedActor: actors.dropped,
        mine: actors.ids.includes(userId),
      }),
    )
  })
  return rows.filter((r) => r !== null)
}

/**
 * The kinds the watch summary's `feed` can carry, as V2-7a (spec §6) names
 * them.
 *
 * ⚠️ AN UNKNOWN KIND IS DROPPED, same rule as `MUSIC_KINDS` above and for the
 * same reason: a row this page cannot describe is a row it would describe
 * wrongly, and the watch service is free to add one before this file learns
 * the word for it.
 */
const WATCH_KINDS = new Set([
  'listed',
  'member_watched',
  'crew_finished',
  'review',
  'season_announced',
  'season_champion',
])

export function watchEvents(shared, userId) {
  const events = Array.isArray(shared?.feed) ? shared.feed : []
  const rows = []
  events.forEach((event) => {
    if (!event || typeof event !== 'object') return
    const kind = typeof event.kind === 'string' ? event.kind : ''
    if (!WATCH_KINDS.has(kind)) return
    const actors = actorsOf(event.member_ids)
    const subscriptionId =
      typeof event.subscription_id === 'string' && event.subscription_id.trim() !== ''
        ? event.subscription_id
        : null
    const season = event.season && typeof event.season === 'object' ? event.season : null
    /**
     * ═══════════════════════════════════════════════════════════════════════
     * 🔴 THE ID: THE PUBLISHED ROW CARRIES NONE, SO ONE IS BUILT HERE
     * ═══════════════════════════════════════════════════════════════════════
     *
     * `row()` already folds `source` and `kind` into its own `id` prefix
     * (`${source}:${kind}:${id}`), so the `id` argument below only has to add
     * what tells two WATCH rows of the SAME kind apart: the title
     * (`subscriptionId`, or the season for `season_champion` — the one kind
     * with no subscription at all, per spec), `at`, and the RAW
     * `member_ids` — not the filtered `actorIds` below, because two rows that
     * differ only in a dropped, unjoinable id must not collide either.
     *
     * Two rows can only share all four of (source, kind, subscription-or-
     * season, at, raw member ids) by being two publishes of the literal same
     * fact, which is not a collision this file owes a tiebreak — it is
     * unique because the ingredients ARE the row, as far as this page can
     * ever tell two rows apart at all.
     */
    const idParts = [
      subscriptionId ?? (season ? `${season.year ?? ''}:${season.season ?? ''}` : ''),
      typeof event.at === 'string' ? event.at : '',
      (Array.isArray(event.member_ids) ? event.member_ids : []).join(','),
    ]
    rows.push(
      row(WATCH_SOURCE, kind, event.at, idParts.join('|'), {
        title: typeof event.title === 'string' && event.title.trim() !== '' ? event.title : null,
        subscriptionId,
        // A whole number the MEMBER entered on `member_watched`, never a
        // derived average — see `ActivityFeed.jsx::RowText`'s own comment
        // where this is drawn, which is where P6-172's refusal actually
        // matters (a rendered sentence pairing a word with a number about a
        // person), not here.
        rating: Number.isInteger(event.rating) ? event.rating : null,
        season,
        actorIds: actors.ids,
        droppedActor: actors.dropped,
        mine: actors.ids.includes(userId),
      }),
    )
  })
  return rows.filter((r) => r !== null)
}

/** Newest first; the row id is the tiebreak so two equal stamps cannot
 * reshuffle between paints. ONE comparator, used both to cap each source at
 * its own newest `SOURCE_MAX` and to order the merge — so the two sorts
 * cannot drift into disagreeing about what "newest" means. */
function byNewest(a, b) {
  return b.instant - a.instant || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
}

/** One source's rows, capped at its own newest `SOURCE_MAX` — sorted first,
 * because a source's own array order is not a promise about time (movie's
 * own `movieEvents` returns nights-then-seasons order, not newest-first), so
 * slicing without sorting would cap the wrong ten. */
function capped(rows) {
  return [...rows].sort(byNewest).slice(0, SOURCE_MAX)
}

/**
 * The whole feed, from all three projections.
 *
 * `movieReady` / `musicReady` / `watchReady` are passed in rather than
 * inferred from the blobs, because the feed has to be able to say *"one of my
 * producers did not answer"* and a caller that unwrapped them first would
 * have thrown that away. A movie-only feed is the thing this row refused; it
 * must never be reachable by accident and indistinguishable from the real
 * one.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 P6-297: `movieLoading` / `musicLoading` / `watchLoading` — A THIRD FACT,
 *    NOT A SECOND SPELLING OF `Ready`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Before this row, a caller had exactly one bit per source — `Ready` — so
 * "still waiting for the first answer" and "answered, and the answer was a
 * failure" were the same bit, and `missing` (below) could not help conflating
 * them: `ActivityFeed.jsx` computed `movieReady` while `movie.state` was
 * still `'loading'`, got `false`, and this function had no way to tell that
 * apart from `'unavailable'`. That is gotcha 1's rule, broken on this exact
 * seam: *"I could not look"*, *"I have not looked yet"* and *"I looked and
 * found none"* are three different facts and a two-valued input can only ever
 * carry two of them.
 *
 * Each `…Loading` flag defaults to `false` — deliberately, so a caller that
 * predates this row (or a test that never mentions loading) keeps EXACTLY
 * today's meaning: not ready is missing. Only a caller that positively knows
 * a source is still in flight can suppress that source's entry in `missing`.
 *
 * Returns `{ rows, hidden, sources, missing, droppedIds }`.
 */
export function activityFeed({
  movieShared,
  musicShared,
  watchShared,
  movieReady,
  musicReady,
  watchReady,
  movieLoading = false,
  musicLoading = false,
  watchLoading = false,
  userId,
}) {
  const who = joinableId(userId)
  // 🔴 EACH SOURCE CAPPED AT ITS OWN NEWEST `SOURCE_MAX` BEFORE THE MERGE —
  // see that constant's own comment for why, and `FEED_MAX`'s for what it
  // does and does not guarantee once there are three sources.
  const rows = [
    ...(movieReady ? capped(movieEvents(movieShared, who)) : []),
    ...(musicReady ? capped(musicEvents(musicShared, who)) : []),
    ...(watchReady ? capped(watchEvents(watchShared, who)) : []),
  ]
  // NEWEST FIRST, ACROSS PRODUCERS THAT STAMP DIFFERENTLY. `instant` is
  // already normalised by `freshness.js::instantOf`, which is where the
  // naive-means-UTC decision is written down.
  rows.sort(byNewest)
  const held = rows.slice(0, FEED_MAX)
  return {
    rows: held,
    // A COUNT OF ROWS THIS MODULE HOLDS, never an estimate of what happened in
    // the server. Expanding the disclosure shows exactly this many more, which
    // is what makes it a count somebody can check rather than a claim.
    hidden: Math.max(0, held.length - FEED_ROWS),
    sources: new Set(held.map((r) => r.source)),
    // ⚠️ WHICH PRODUCER DID NOT ANSWER, so the page can say so. An absent
    // source and an empty one are different facts and the feed names which —
    // gotcha 1's rule, on a surface instead of in a test.
    //
    // 🔴 P6-297: A SOURCE STILL LOADING IS NEITHER READY NOR MISSING. `missing`
    // names only a source whose read has FINISHED without landing `ready` —
    // unavailable, misconfigured, or any state this module has no name for at
    // all. An unrecognised state resolves here to `!ready && !loading`, i.e.
    // to MISSING, on purpose: a state nobody wrote a name for yet is closer to
    // "answered with something unusable" than to "still waiting" — gotcha 1
    // again, from the other side, so a caller cannot make an unfamiliar answer
    // read as more hopeful than a known failure just because it is unfamiliar.
    missing: [
      ...(movieReady || movieLoading ? [] : [MOVIE_SOURCE]),
      ...(musicReady || musicLoading ? [] : [MUSIC_SOURCE]),
      ...(watchReady || watchLoading ? [] : [WATCH_SOURCE]),
    ],
    droppedIds: held.some((r) => r.droppedActor),
  }
}

/**
 * The stamp over a view built from several projections.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 P6-306: THE NEWEST OF THEM — AND THE OLD RULE SURVIVES ONLY AS A NARROW
 *    GUARD, `staleSources` BELOW
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Jordan, 2026-10-01: *"label the feed by its newest source"*.
 *
 * This used to be the OLDEST, on the argument that a merged view is exactly as
 * fresh as its stalest input. The argument was right about what it guarded and
 * wrong about what it cost: the home feed read *"AS OF 05:11 UTC · 21 H AGO"*
 * directly above items from three hours earlier. The bot's publisher rewrites
 * an UNCHANGED object only once its `as_of` passes a 24-hour floor
 * (`cogs/projections.py::AS_OF_MAX_AGE_SECONDS`), so on a quiet movie week one
 * source's stamp climbs toward a day old while the rows under it are hours old,
 * and the oldest-stamp rule printed it — a label that was accurate about a
 * number nobody was asking about.
 *
 * ✅ WHAT THE OLD RULE WAS FOR, AND WHERE THAT WENT: it kept a STUCK source from
 * hiding behind fresh ones. That need is real and is not gone, only narrower.
 * A healthy source re-stamps at least once a day, so one far past that floor is
 * genuinely stale, and `staleSources` names it.
 *
 * ⚠️ WHAT THE LABEL NO LONGER PROMISES, SAID OUT LOUD: it is the freshest
 * evidence the feed holds, not a bound on every part of it. A source older than
 * the bot's floor but inside `STALE_SOURCE_HOURS` can be older than the label
 * reads, and nothing on the page says so. That is the trade, and it is why the
 * threshold is a contract with the bot rather than a number.
 *
 * `feedAsOf` never counted its arguments (V2-7b), and still does not: a third
 * stamp is just a third element in the array the caller passes. The CALLER
 * passes only READY sources' stamps — a loading or failed source must never be
 * the one shown, and with "newest" that matters more than it did, because a
 * pending source's `as_of` could now win outright. See `ActivityFeed.jsx`.
 *
 * `null` when none of the sources has a readable stamp, so the caller renders
 * the same "no date on this" sentence every other module on this page renders.
 */
export function feedAsOf(stamps) {
  const usable = (Array.isArray(stamps) ? stamps : [])
    .filter((iso) => typeof iso === 'string')
    .map((iso) => ({ iso, ms: instantOf(iso) }))
    .filter((s) => s.ms !== null)
  if (usable.length === 0) return null
  return usable.reduce((newest, s) => (s.ms > newest.ms ? s : newest)).iso
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 P6-306: HOURS PAST WHICH A READY SOURCE'S OWN STAMP IS CALLED OUT BY NAME —
 *    AND THE NUMBER IS A CONTRACT WITH THE BOT, NOT A SETTING (GOTCHA 21)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 48: twice the 24-hour floor the bot's publisher re-stamps an unchanged object
 * at (`cogs/projections.py::AS_OF_MAX_AGE_SECONDS`). The oldest a HEALTHY
 * publisher leaves a stamp is that floor plus its hourly reconcile pass and one
 * tick — about 25 hours — so a healthy source is never near this number, and a
 * source whose stamp has not moved in two days plainly is.
 *
 * ⛔ IT MUST EXCEED THAT FLOOR, AND THE FLOOR IS NOT DEFINED HERE. If the bot
 * ever raised it past this number, a perfectly healthy source would cross the
 * line every day and the feed would cry stale about a publisher doing exactly
 * what it was built to. Nothing on this side can see the bot's constant — it is
 * another deployable — so `tests/test_feed_stamp_threshold.py` reads THIS LINE
 * by regex and compares (against the floor, and against the floor plus that
 * hourly pass), and the build reds on the day the numbers meet.
 *
 * ⚠️ HENCE THE SHAPE: a plain integer, on one line, in HOURS. Rewriting it as an
 * expression, or in other units, makes that test fail loudly rather than guess.
 * It is hours so the bot-side conversion to seconds is one visible multiply.
 *
 * ⚠️ AT LEAST 48 FOR A SECOND REASON, WHICH IS GRAMMAR: `ActivityFeed.jsx` says
 * "N days", plural, with N the age floored to whole days, so any threshold under
 * 48 hours can print "1 days old". `activity.test.js` pins that assumption.
 */
export const STALE_SOURCE_HOURS = 48

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

/**
 * The READY sources whose own `as_of` is older than `STALE_SOURCE_HOURS`, as
 * `{ source, days }` PARTS, in the order the caller passed them. Empty when
 * every one is inside the window — the feed says nothing extra in that case.
 *
 * `stampsBySource` maps a source to its `as_of`, with the same caller contract
 * as `feedAsOf`: only READY sources go in. Like that function it never counts
 * its arguments, so a fourth source is a fourth key.
 *
 * ⚠️ PARTS, NOT A SENTENCE, for the rule `ActivityFeed.jsx`'s own header states:
 * the words belong to the file that draws them. `days` is the age floored to
 * whole days, so it never overstates; at a threshold of 48 hours it is always
 * at least two.
 *
 * 🔴 A SOURCE WITH NO READABLE STAMP IS NOT A STALE ONE. Its age is unknown, and
 * a feed that claimed *"4 days old"* about a stamp it could not parse would be
 * inventing the number — the failure `freshness()` refuses by name. It is also
 * why such a source is silent here, as it was silent in the label before this
 * row; the absence is recorded rather than quietly changed.
 *
 * A stamp from the FUTURE is a clock disagreement, not a freshness fact, and
 * `freshness()` already names it ("clock ahead") — a negative age is simply
 * never over the threshold.
 *
 * `nowMs` falls back to the real clock when it is not a finite number, the same
 * trade `freshness()` and `sinceLabel()` make, so a caller that forgot it gets
 * the right answer instead of `NaN` quietly comparing false.
 */
export function staleSources(stampsBySource, nowMs) {
  const now = Number.isFinite(nowMs) ? nowMs : Date.now()
  const limit = STALE_SOURCE_HOURS * HOUR_MS
  const stale = []
  for (const [source, iso] of Object.entries(stampsBySource ?? {})) {
    const at = instantOf(iso)
    if (at === null) continue
    const age = now - at
    // STRICTLY older: a source exactly at the threshold is still inside it.
    if (age > limit) stale.push({ source, days: Math.floor(age / DAY_MS) })
  }
  return stale
}
